import pick from 'lodash/pick';
import buildRowInput from './_buildRowInput';

export function extractGraphQLErrorMessage(err) {
  const graphQLError = err && err.graphQLErrors && err.graphQLErrors[0];
  if (!graphQLError || !graphQLError.message) return null;

  const {message} = graphQLError;

  // Existing server behavior sometimes wraps Epicor errors as JSON in the message.
  const jsonStartIdx = message.indexOf('{');
  if (jsonStartIdx >= 0) {
    try {
      const parsed = JSON.parse(message.substring(jsonStartIdx));
      if (parsed && parsed.ErrorMessage) return parsed.ErrorMessage;
    } catch (e) {
      // Fall through to raw message.
    }
  }

  return message.replace(/^GraphQL error:\s*/, '');
}

// Builds the entry payload sent to saveTime / saveTimeBatch for a given cell
// edit.  Returns { skip: true } when the edit is a no-op (typing 0 in an empty
// cell, for example) so callers can bail out without touching the server or
// the queue.
export function buildSaveTimeEntry({
  timesheet,
  rowIndex,
  entryIndex,
  detailIndex = 0,
  hours,
  overnightCompensation,
  isGig
}) {
  const row = timesheet.rows[rowIndex];
  const sourceEntry = row.entries[entryIndex];
  const entry = {
    ...pick(sourceEntry, [
      'OvernightCompensation',
      'Date',
      'id'
    ]),
    // Derive current gig state from PayType so we don't need IsGig in the query
    IsGig: sourceEntry.PayType === 'GIG',
    ...pick(sourceEntry.Detail[detailIndex], [
      'TimeAllocation',
      'id'
    ])
  };
  if (hours === 0 && entry.TimeAllocation === 0 && isGig === undefined) {
    // typing 0 in a cell that was already empty - nothing to save
    return {skip: true};
  }
  if (hours !== undefined) entry.TimeAllocation = hours;
  if (overnightCompensation !== undefined) {
    entry.OvernightCompensation = overnightCompensation;
    // we need to make sure we save a 0-hour entry, otherwise we'll just have
    // the header and it won't work in Epicor
    if (!entry.TimeAllocation || entry.TimeAllocation === 0.01) {
      entry.TimeAllocation = 0.01;
      entry.OvernightCompensationOff = overnightCompensation;
    }
  }
  if (isGig !== undefined) entry.IsGig = isGig;
  return {skip: false, row, entry};
}

// Builds the GraphQL variables payload (sans `submit`) for a single item.
export function buildSaveTimeVariables(timesheet, row, entry) {
  return {
    ...buildRowInput(timesheet, row),
    entry
  };
}

// Applies a saveTime / saveTimeBatch result for a single item to the Apollo
// cache via the updateTimesheetData handler.  Shared between the single-save
// mutation update callback and the batch flush handler.
export function applySaveTimeResultToCache({
  updateTimesheetData,
  store,
  row,
  rowIndex,
  entryIndex,
  detailIndex = 0,
  saveTime
}) {
  const entryUpdates = pick(saveTime, [
    'id',
    'InLieuType',
    'OvernightCompensation',
    'PayType',
    'Submitted',
    'LaborHed'
  ]);
  const detailUpdates = pick(saveTime, [
    'id',
    'TimeAllocation',
    'ClockInTime',
    'ClockOutTime',
    'ClockInDate',
    'ClockOutDate'
  ]);
  detailUpdates.__typename = 'TimesheetEntryDetail';
  const detailOp = row.entries[entryIndex].Detail[detailIndex]
    ? {
        [detailIndex]: {$merge: detailUpdates}
      }
    : {
        $push: [
          {
            ClockInTime: 0,
            ClockOutTime: 0,
            ClockInDate: '',
            ClockOutDate: '',
            Narrative: '',
            ...detailUpdates
          }
        ]
      };
  updateTimesheetData(
    {
      rows: {
        [rowIndex]: {
          entries: {
            [entryIndex]: {
              // could include a new id, so we need an explicit merge
              $merge: entryUpdates,
              Detail: detailOp
            }
          }
        }
      }
    },
    store
  );
}

// Applies an optimistic update to the Apollo cache for a queued (not-yet-sent)
// cell edit so the UI reflects the user's change while auto-save is disabled.
// Only touches the fields that the user just changed; the eventual server
// response will backfill ids and computed fields via applySaveTimeResultToCache.
export function applyOptimisticEntryToCache({
  updateTimesheetData,
  rowIndex,
  entryIndex,
  detailIndex = 0,
  entry,
  row
}) {
  const entryUpdates = {};
  if (entry.OvernightCompensation !== undefined) {
    entryUpdates.OvernightCompensation = entry.OvernightCompensation;
  }
  if (entry.IsGig !== undefined) {
    entryUpdates.PayType = entry.IsGig ? 'GIG' : 'REG';
  }
  const detailUpdates = {
    TimeAllocation: entry.TimeAllocation,
    __typename: 'TimesheetEntryDetail'
  };
  const detailOp = row.entries[entryIndex].Detail[detailIndex]
    ? {[detailIndex]: {$merge: detailUpdates}}
    : {
        $push: [
          {
            id: entry.id,
            ClockInTime: 0,
            ClockOutTime: 0,
            ClockInDate: '',
            ClockOutDate: '',
            Narrative: '',
            ...detailUpdates
          }
        ]
      };
  const entryMerge = Object.keys(entryUpdates).length
    ? {$merge: entryUpdates, Detail: detailOp}
    : {Detail: detailOp};
  updateTimesheetData({
    rows: {
      [rowIndex]: {
        entries: {
          [entryIndex]: entryMerge
        }
      }
    }
  });
}

export function pendingSaveKey({rowIndex, entryIndex, detailIndex = 0}) {
  return `${rowIndex}:${entryIndex}:${detailIndex}`;
}
